import React from 'react';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import HOUSEHOLD_DESIGNATION_CODES from 'constants/householdDesignationCodes';
import AFFORDABLE_RELATIONSHIPS from 'constants/affordableRelationships';
import APPLICANT_TYPES from 'constants/applicantTypes';
import { MINORS_RELATIONSHIP_FIELD_NAMES } from 'pages/AffordaHousehold/AffordaHousehold.MinorsRelationship.constants';
import { validateNoSpecialChars } from 'utils/validateNoSpecialChars';

export const GridProps = {
  xs: 12,
  sm: 6,
  md: 6,
};

export const FIELD_NAMES = {
  // Common fields
  firstName: 'firstName',
  middleName: 'middleName',
  lastName: 'lastName',
  birthday: 'birthday',
  phoneNumber: 'phoneNumber',
  emailAddress: 'emailAddress',

  // Member type fields
  applicantType: 'applicantType',
  isPrimary: 'isPrimary',

  // Designation and relationship fields
  householdDesignation: 'householdDesignation',
  affordableRelationship: 'affordableRelationship',
  relationship: 'relationship',

  // Status fields
  studentStatus: 'studentStatus',
  maritalStatus: 'maritalStatus',

  // UI state
  isOpen: 'isOpen',
};

export const NEW_ADULT_MEMBER = {
  [FIELD_NAMES.applicantType]: '',
  [FIELD_NAMES.isPrimary]: false,
  [FIELD_NAMES.firstName]: '',
  [FIELD_NAMES.middleName]: '',
  [FIELD_NAMES.lastName]: '',
  [FIELD_NAMES.birthday]: '',
  [FIELD_NAMES.phoneNumber]: '',
  [FIELD_NAMES.householdDesignation]: '',
  [FIELD_NAMES.affordableRelationship]: '',
  [FIELD_NAMES.studentStatus]: '',
  [FIELD_NAMES.relationship]: '',
  [FIELD_NAMES.emailAddress]: '',
  [FIELD_NAMES.isOpen]: true,
};

export const NEW_MINOR_MEMBER = {
  [FIELD_NAMES.applicantType]: APPLICANT_TYPES.MINOR,
  [FIELD_NAMES.firstName]: '',
  [FIELD_NAMES.lastName]: '',
  [FIELD_NAMES.birthday]: '',
  [FIELD_NAMES.householdDesignation]: HOUSEHOLD_DESIGNATION_CODES.CHILD,
  [MINORS_RELATIONSHIP_FIELD_NAMES.legallyResponsible]: [],
  [FIELD_NAMES.affordableRelationship]: AFFORDABLE_RELATIONSHIPS.CHILD,
  [FIELD_NAMES.isOpen]: true,
};

export const FIELDS = ({
  id,
  memberId,
  isMinor,
  intl,
  validatePhoneNumber,
  validateEmail,
}) => {
  return [
    {
      id,
      variant: 'text',
      label: (
        <FormattedMessage id="afforda.formHouseholdProfile.firstNameLabel" />
      ),
      name: memberId
        ? `${memberId}.${FIELD_NAMES.firstName}`
        : FIELD_NAMES.firstName,
      rules: {
        required: true,
        validate: validateNoSpecialChars,
      },
      GridProps,
    },
    {
      id,
      variant: 'text',
      label: (
        <FormattedMessage id="afforda.formHouseholdProfile.lastNameLabel" />
      ),
      name: memberId
        ? `${memberId}.${FIELD_NAMES.lastName}`
        : FIELD_NAMES.lastName,
      rules: {
        required: true,
        validate: validateNoSpecialChars,
      },
      GridProps,
    },
    {
      id,
      variant: 'date',
      label: (
        <FormattedMessage id="afforda.formHouseholdProfile.birthDayLabel" />
      ),
      name: memberId
        ? `${memberId}.${FIELD_NAMES.birthday}`
        : FIELD_NAMES.birthday,
      disableFuture: true,
      openTo: 'year',
      views: ['year', 'month', 'day'],
      helperText: '',
      defaultCalendarMonth: moment(2000, 'YYYY'),
      rules: {
        required: true,
        minDate: isMinor ? moment().subtract(18, 'years') : undefined,
        maxDate: !isMinor ? moment().subtract(18, 'years') : undefined,
        validate: (value) => {
          if (!moment(value).isValid()) {
            return intl.formatMessage({
              id: 'afforda.formHouseholdProfile.birthdayInvalid',
            });
          }
          // must be in the past
          if (moment(value).isAfter(moment())) {
            return intl.formatMessage({
              id: 'afforda.formHouseholdProfile.birthdayPast',
            });
          }

          if (isMinor) {
            // must be less than 18 years old
            if (moment().diff(moment(value), 'years') >= 18) {
              return intl.formatMessage({
                id: 'afforda.formHouseholdProfile.birthdayUnder18',
              });
            }
          }
        },
      },
      GridProps,
    },
    ...(!isMinor
      ? [
          {
            id,
            variant: 'phone',
            name: memberId
              ? `${memberId}.${FIELD_NAMES.phoneNumber}`
              : FIELD_NAMES.phoneNumber,
            label: <FormattedMessage id="app.label.phoneNumber" />,
            GridProps: {
              ...GridProps,
              sm: 6,
              md: 6,
            },
            rules: {
              required: true,
              validate: validatePhoneNumber,
            },
          },
          {
            id,
            variant: 'email',
            name: memberId
              ? `${memberId}.${FIELD_NAMES.emailAddress}`
              : FIELD_NAMES.emailAddress,
            label: (
              <FormattedMessage id="afforda.formHouseholdProfile.emailLabel" />
            ),
            GridProps: {
              ...GridProps,
              sm: 6,
              md: 6,
            },
            rules: {
              required: true,
              validate: validateEmail,
            },
          },
        ]
      : []),
  ];
};
